/**
 * Pure helpers for the visual Mapping Rule Builder. A "spec" is the same JSON the processor executes
 * (see src/utils/mappingEngine.js — the client mirror of showroompricing-file-processor's mapping.ts).
 * These helpers translate between that JSON and the visual editor's per-field rule editing, and are
 * careful to PRESERVE any rule the visual UI can't represent (nested if/then/else, complex expr) so
 * round-tripping a spec through the builder never loses data.
 */

// Rule "kinds" the visual editor can render. ADVANCED = present but only editable in the JSON view.
export const RULE_KINDS = {
  UNMAPPED: 'unmapped',
  FROM: 'from',
  CONST: 'const',
  CONCAT: 'concat',
  TODAY: 'today',
  EXPR: 'expr',
  ADVANCED: 'advanced',
};

export const RULE_MODE_OPTIONS = [
  { value: RULE_KINDS.UNMAPPED, label: 'Unmapped' },
  { value: RULE_KINDS.FROM, label: 'From source column' },
  { value: RULE_KINDS.CONST, label: 'Constant value' },
  { value: RULE_KINDS.CONCAT, label: 'Concatenate columns' },
  { value: RULE_KINDS.TODAY, label: "Today's date" },
  { value: RULE_KINDS.EXPR, label: 'Expression (math)' },
];

// Transform ops (mirror applyTransforms in mappingEngine.js). Each entry knows its param fields.
export const TRANSFORM_OPS = [
  { op: 'trim', label: 'Trim', params: [] },
  { op: 'upper', label: 'Uppercase', params: [] },
  { op: 'lower', label: 'Lowercase', params: [] },
  { op: 'substring', label: 'Substring', params: ['start', 'end'] },
  { op: 'replace', label: 'Replace', params: ['find', 'with'] },
  { op: 'map', label: 'Map values', params: ['table'] },
];

// Filter ops (mirror passesFilter in mappingEngine.js). needsValue drives whether to show a value box.
export const FILTER_OPS = [
  { op: 'nonEmptyNonZero', label: 'is not empty / not zero', needsValue: false },
  { op: 'nonEmpty', label: 'is not empty', needsValue: false },
  { op: 'isEmpty', label: 'is empty', needsValue: false },
  { op: 'nonZero', label: 'is not zero', needsValue: false },
  { op: 'equals', label: 'equals', needsValue: true },
  { op: 'notEquals', label: 'does not equal', needsValue: true },
  { op: 'dateNotFuture', label: 'is a date not in the future', needsValue: false },
];

export const EXPR_OPS = [
  { op: 'mul', label: 'Multiply (×)' },
  { op: 'add', label: 'Add (+)' },
  { op: 'sub', label: 'Subtract (−)' },
  { op: 'coalesce', label: 'First non-empty' },
];

const EXPR_OP_KEYS = EXPR_OPS.map((o) => o.op);

// An expr is "simple" (editable in the visual editor) when it is a single add/sub/mul/coalesce over a
// flat list of {col} / {const} operands. Anything nested falls back to ADVANCED (JSON-only).
export function isSimpleExpr(expr) {
  if (!expr || typeof expr !== 'object') return false;
  const opKey = EXPR_OP_KEYS.find((k) => k in expr);
  if (!opKey) return false;
  const operands = expr[opKey];
  if (!Array.isArray(operands) || !operands.length) return false;
  return operands.every((o) => o && typeof o === 'object' && ('col' in o || 'const' in o));
}

export function detectRuleKind(rule) {
  if (rule == null || typeof rule !== 'object') return RULE_KINDS.UNMAPPED;
  if ('if' in rule) return RULE_KINDS.ADVANCED;
  if ('expr' in rule) return isSimpleExpr(rule.expr) ? RULE_KINDS.EXPR : RULE_KINDS.ADVANCED;
  if ('concat' in rule) return RULE_KINDS.CONCAT;
  if ('today' in rule) return RULE_KINDS.TODAY;
  if ('const' in rule) return RULE_KINDS.CONST;
  if ('from' in rule) return RULE_KINDS.FROM;
  return RULE_KINDS.ADVANCED;
}

// Build a fresh rule for a chosen mode, carrying over anything sensible from the previous rule so
// switching modes doesn't wipe the user's work (e.g. keep the source column and transforms).
export function ruleForKind(kind, prev) {
  const p = prev && typeof prev === 'object' ? prev : {};
  switch (kind) {
    case RULE_KINDS.UNMAPPED:
      return undefined;
    case RULE_KINDS.FROM: {
      const from = p.from || (Array.isArray(p.concat) ? p.concat[0] : '') || '';
      const rule = { from };
      if (Array.isArray(p.transform) && p.transform.length) rule.transform = p.transform;
      if (p.default !== undefined) rule.default = p.default;
      return rule;
    }
    case RULE_KINDS.CONST:
      return { const: typeof p.const === 'string' ? p.const : '' };
    case RULE_KINDS.CONCAT: {
      const concat = Array.isArray(p.concat) ? p.concat : (p.from ? [p.from] : ['']);
      const rule = { concat, delimiter: p.delimiter != null ? p.delimiter : ' ' };
      if (Array.isArray(p.transform) && p.transform.length) rule.transform = p.transform;
      return rule;
    }
    case RULE_KINDS.TODAY:
      return { today: p.today || 'YYYY-MM-DD' };
    case RULE_KINDS.EXPR: {
      if ('expr' in p && isSimpleExpr(p.expr)) return { expr: p.expr };
      const seed = p.from ? { col: p.from } : { const: '' };
      return { expr: { mul: [seed, { const: '1' }] } };
    }
    default:
      return p;
  }
}

// Short human summary of a rule for the target-column slot list.
export function summarizeRule(rule) {
  const kind = detectRuleKind(rule);
  const tx = Array.isArray(rule?.transform) && rule.transform.length
    ? ` ▸ ${rule.transform.map(transformLabel).join(' ▸ ')}` : '';
  switch (kind) {
    case RULE_KINDS.UNMAPPED: return 'unmapped';
    case RULE_KINDS.FROM: return `← ${rule.from || '(none)'}${tx}`;
    case RULE_KINDS.CONST: return `= "${rule.const}"`;
    case RULE_KINDS.CONCAT: return `← ${(rule.concat || []).join(' + ')}${tx}`;
    case RULE_KINDS.TODAY: return `= today (${rule.today})`;
    case RULE_KINDS.EXPR: {
      const opKey = EXPR_OP_KEYS.find((k) => k in rule.expr) || '';
      const label = EXPR_OPS.find((o) => o.op === opKey)?.label || opKey;
      return `= ${label.replace(/ \(.*\)/, '')}(${(rule.expr[opKey] || []).map(exprOperandLabel).join(', ')})`;
    }
    default: return 'advanced rule (edit in JSON)';
  }
}

export function transformLabel(t) {
  if (!t || !t.op) return '?';
  switch (t.op) {
    case 'substring': return `substring ${t.start ?? ''}–${t.end ?? ''}`;
    case 'replace': return `replace "${t.find ?? ''}"→"${t.with ?? ''}"`;
    case 'map': return `map (${Object.keys(t.table || {}).length})`;
    default: return t.op;
  }
}

export function exprOperandLabel(o) {
  if (o && 'col' in o) return o.col || '(col)';
  if (o && 'const' in o) return String(o.const);
  return '?';
}

function collectExprCols(e, acc) {
  if (!e || typeof e !== 'object') return;
  if (e.col) acc.add(e.col);
  ['add', 'sub', 'mul', 'coalesce'].forEach((k) => Array.isArray(e[k]) && e[k].forEach((x) => collectExprCols(x, acc)));
}

// Source columns a single field rule reads from (from/concat/expr, recursing into if/then/else).
export function collectSourceCols(rule, acc = new Set()) {
  if (!rule || typeof rule !== 'object') return acc;
  if (rule.from) acc.add(rule.from);
  if (Array.isArray(rule.concat)) rule.concat.forEach((c) => c && acc.add(c));
  if (rule.expr) collectExprCols(rule.expr, acc);
  if (rule.if) { collectSourceCols(rule.then, acc); collectSourceCols(rule.else, acc); }
  return acc;
}

// Fresh transform of a given op with empty params.
export function emptyTransform(op) {
  switch (op) {
    case 'substring': return { op, start: 0, end: 1 };
    case 'replace': return { op, find: '', with: '' };
    case 'map': return { op, table: {} };
    default: return { op };
  }
}

// ---- spec-level helpers ----

export const STARTER_SPEC = {
  version: 1,
  name: '',
  outputColumns: [],
  rowFilters: [],
  outputs: [{ fields: {} }],
  ui: { outputLabels: ['Output 1'] },
};

// Guarantee the shape the editor relies on without mutating semantics the engine reads.
export function normalizeSpec(spec) {
  const s = spec && typeof spec === 'object' ? { ...spec } : {};
  if (!Array.isArray(s.outputs) || !s.outputs.length) s.outputs = [{ fields: {} }];
  s.outputs = s.outputs.map((o) => ({ ...o, fields: o && o.fields ? o.fields : {} }));
  if (!Array.isArray(s.rowFilters)) s.rowFilters = [];
  if (!Array.isArray(s.fillDown)) s.fillDown = [];
  const labels = Array.isArray(s.ui?.outputLabels) ? s.ui.outputLabels.slice(0, s.outputs.length) : [];
  while (labels.length < s.outputs.length) labels.push(`Output ${labels.length + 1}`);
  s.ui = { ...(s.ui || {}), outputLabels: labels };
  return s;
}

// Recompute outputColumns as every mapped target across all outputs, in canonical (targetOrder) order,
// with any extra mapped keys appended. Keeps the preview column order stable and meaningful.
export function computeOutputColumns(spec, targetOrder) {
  const mapped = new Set();
  (spec.outputs || []).forEach((o) => Object.keys(o.fields || {}).forEach((k) => mapped.add(k)));
  const ordered = (targetOrder || []).filter((k) => mapped.has(k));
  mapped.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
  return ordered;
}

// Serialize the editor spec back to the exact JSON shape the processor/saveSpec expect. Drops empty
// helper arrays so specs stay clean; keeps ui.outputLabels (engine ignores unknown keys).
export function serializeSpec(spec, targetOrder) {
  const s = normalizeSpec(spec);
  const out = {
    version: s.version || 1,
    name: s.name || '',
    outputColumns: computeOutputColumns(s, targetOrder),
    outputs: s.outputs.map((o) => {
      const t = { fields: o.fields || {} };
      if (Array.isArray(o.when) && o.when.length) t.when = o.when;
      return t;
    }),
  };
  if (s.rowFilters.length) out.rowFilters = s.rowFilters;
  if (s.fillDown.length) out.fillDown = s.fillDown;
  if (s.fileSettings) out.fileSettings = s.fileSettings;
  out.ui = { outputLabels: s.ui.outputLabels };
  return out;
}
